import { Component, Input, OnInit } from "@angular/core";
import { CommonModule } from '@angular/common';
import { NgbNavModule } from '@ng-bootstrap/ng-bootstrap';
import { BackendApiService } from "../../../../../app/services/backend-api.service";
import { BaseUrlConstantsService } from "../../../../../app/services/base-url-constants.service";
import { SpectraChartComponent } from "../../spectra-chart/spectra-chart.component";

@Component({
  selector: 'app-velocity-spectra',
  standalone: true,
  imports: [CommonModule, NgbNavModule, SpectraChartComponent],
  templateUrl: './velocity-spectra.component.html',
  styleUrl: './velocity-spectra.component.scss'
})
export class VelocitySpectraComponent implements OnInit {

  @Input() data: any;
  public clientId: string | null = null;

  // -- CHART 1: VELOCITY AMPLITUDE --
  public loadingVelocityAmplitude = false;
  public velocityData: any = null;
  public canvasDataSeries: any[] = [];

  // -- CHART 2: VELOCITY ACCELERATION --
  public loadingVelocityAcceleration = false;
  public accelerationData: any = null;
  public canvasAccelerationSeries: any[] = [];

  // -- TABLE 1: CREST FACTOR --
  public loadingCrestFactor = false;
  public crestFactorData: any = null;

  // -- TABLE 2: PREDOMINANT FREQUENCY --
  public loadingPredominantFrequency = false;
  public predominantFrequencyData: any[] = [];
  public FFTId = sessionStorage.getItem('FFTId');
  public KurtosisId = sessionStorage.getItem('KurtosisId')

  constructor(private service: BackendApiService) {
    this.clientId = localStorage.getItem('user_id');
    console.log("----Velocity spectra--", this.data)
  }

  ngOnInit(): void {
    this.fetchVelocityAmplitudeSpectra();
    this.fetchVelocityAccelerationSpectra();
    this.fetchCrestFactor();
    this.fetchPredominantFrequency();
  }

  // --- 1. VELOCITY AMPLITUDE ---
  async fetchVelocityAmplitudeSpectra() {
    try {
      this.loadingVelocityAmplitude = true;
      const apiUrl = `${BaseUrlConstantsService.PIEZO_DIAGNOSTICS_FFT_GRAPH_API}${this.FFTId}?type=velocity&userId=${this.clientId}&asset_type=${this.data.assetType}&axis=PARALLEL_SIDE`;
      const response: any = await this.service.getDataV2(apiUrl);

      if (response?.data?.status === "success") {
        this.velocityData = response.data.body;
        this.canvasDataSeries = this.mapToCanvasSeries(this.velocityData);
      }
    } catch (error) {
      console.error("Error loading velocity amplitude spectra", error);
    } finally {
      this.loadingVelocityAmplitude = false;
    }
  }

  // --- 2. VELOCITY ACCELERATION ---
  async fetchVelocityAccelerationSpectra() {
    try {
      this.loadingVelocityAcceleration = true;
      const apiUrl = `${BaseUrlConstantsService.PIEZO_DIAGNOSTICS_FFT_CHART_API}${this.FFTId}/${this.clientId}/PARALLEL_SIDE`;
      const response: any = await this.service.getDataV2(apiUrl);

      if (response?.data?.status === "success") {
        this.accelerationData = response.data.body;
        this.canvasAccelerationSeries = this.mapToCanvasSeries(this.accelerationData);
      }
    } catch (error) {
      console.error("Error loading velocity acceleration spectra", error);
    } finally {
      this.loadingVelocityAcceleration = false;
    }
  }

  // --- 3. CREST FACTOR ---
  async fetchCrestFactor() {
    try {
      this.loadingCrestFactor = true;
      const apiUrl = `${BaseUrlConstantsService.PIEZO_DIAGNOSTICS_FFT_CREST_FACTOR_API}${this.FFTId}`;
      const response: any = await this.service.getDataV2(apiUrl);

      if (response?.status === "success" || response?.data?.status === "success") {
         const body = response.body || response.data?.body;
         this.crestFactorData = body?.result;
      }
    } catch (error) { 
      console.error("Error loading crest factor", error);
    } finally {
      this.loadingCrestFactor = false;
    }
  }

  // --- 4. PREDOMINANT FREQUENCY ---
  async fetchPredominantFrequency() {
    try {
      this.loadingPredominantFrequency = true;
      const apiUrl = `${BaseUrlConstantsService.PIEZO_DIAGNOSTICS_FFT_PREDOMINANT_API}43,44,45/${this.FFTId}?type=velocity&userId=${this.clientId}&assetType=${this.data.assetType}`;

      const response: any = await this.service.getDataV2(apiUrl);
      console.log("Predominant Frequency Response: ", response);

      if (response?.data?.status === "success") {
        this.predominantFrequencyData = response.data.body || [];
      }
    } catch (error) {
      console.error("Error loading predominant frequency", error);
    } finally {
      this.loadingPredominantFrequency = false;
    }
  }

  // --- HELPER ---
  private mapToCanvasSeries(apiBody: any): any[] {
    if (!apiBody) return [];
    const { chartXdata, chartYdata, chartZdata, labelx, labely, labelz } = apiBody;

    return [
      {
        type: "line",
        name: labelx || 'Axial',
        showInLegend: true,
        color: '#1e88e5',
        lineThickness: 2,
        markerSize: 0,
        xValueType: "number",
        dataPoints: chartXdata?.map((p: any) => ({ x: p.x, y: p.y })) || []
      },
      {
        type: "line",
        name: labely || 'Tangential (Vertical)',
        showInLegend: true,
        color: '#43a047',
        lineThickness: 2,
        markerSize: 0,
        xValueType: "number",
        dataPoints: chartYdata?.map((p: any) => ({ x: p.x, y: p.y })) || []
      },
      {
        type: "line",
        name: labelz || 'Radial (Horizontal)',
        showInLegend: true,
        color: '#fb8c00',
        lineThickness: 2,
        markerSize: 0,
        xValueType: "number",
        dataPoints: chartZdata?.map((p: any) => ({ x: p.x, y: p.y })) || []
      }
    ];
  }
}
